import { useCallback, useEffect, useRef, useState } from 'react';
import { useHref, useNavigate } from 'react-router-dom';

const isPlainLeftClick = (event: MouseEvent) =>
  0 === event.button && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

/**
 * Warns before the page is left while `when` holds (unsaved changes). The app runs a BrowserRouter,
 * which cannot block navigation, so the two ways out are covered separately:
 * - a reload, a closed tab, a typed URL or a full-page link (the v1 app) get the browser's own
 *   "Leave site?" prompt (beforeunload);
 * - a click on an in-app link is held back (`isLeaveConfirmOpen`), so the page can ask with
 *   its own dialog and then `leave()` or `stay()`.
 * Navigation started from code (navigate() in a click handler) and the browser's back button are
 * not caught.
 */
export const useLeavePageWarning = (when: boolean) => {
  const [pendingPath, setPendingPath] = useState<string | null>(null);

  const isLeavingRef = useRef(false);

  const navigate = useNavigate();
  // The router's basename ('/v2'), so a held-back link is followed inside the app.
  const basename = useHref('/').replace(/\/$/, '');

  useEffect(() => {
    if (!when) {
      return undefined;
    }

    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isLeavingRef.current) {
        return;
      }

      event.preventDefault();
      // Older browsers show the prompt only when returnValue is set.
      event.returnValue = '';
    };

    // Capture phase, so the link's own handler (React Router's) never sees the click.
    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented || !isPlainLeftClick(event)) {
        return;
      }

      const anchor = (event.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement;

      if (
        !anchor ||
        (anchor.target && '_self' !== anchor.target) ||
        anchor.hasAttribute('download')
      ) {
        return;
      }

      const url = new URL(anchor.href, window.location.href);

      // Another site is left by a full page load: beforeunload asks.
      if (url.origin !== window.location.origin || url.href === window.location.href) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      setPendingPath(`${url.pathname}${url.search}${url.hash}`);
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    document.addEventListener('click', onClick, true);

    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('click', onClick, true);
    };
  }, [when]);

  const leave = useCallback(() => {
    if (!pendingPath) {
      return;
    }

    setPendingPath(null);

    if (!basename || pendingPath === basename || pendingPath.startsWith(`${basename}/`)) {
      navigate(pendingPath.slice(basename.length) || '/');

      return;
    }

    // A v1 page on the same domain: a full page load, which the warning has already asked about.
    isLeavingRef.current = true;
    window.location.assign(pendingPath);
  }, [basename, navigate, pendingPath]);

  const stay = useCallback(() => setPendingPath(null), []);

  return { isLeaveConfirmOpen: null !== pendingPath, leave, stay };
};
